import { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { ArtifactAiTranscriptMessage } from '@/hooks/useArtifactAi';
import type { AiAskBody } from '@/lib/api';

interface ArtifactAiAskTabProps {
  transcript: ArtifactAiTranscriptMessage[];
  conversationId: string | null;
  disabled: boolean;
  isStreaming: boolean;
  onAsk: (question: string, opts?: Pick<AiAskBody, 'providerId' | 'model'>) => Promise<void>;
}

export function ArtifactAiAskTab({
  transcript,
  conversationId,
  disabled,
  isStreaming,
  onAsk,
}: ArtifactAiAskTabProps): React.ReactElement {
  const [question, setQuestion] = useState('');
  const canSubmit = !disabled && !isStreaming && question.trim().length > 0;
  const submitQuestion = (): void => {
    const trimmed = question.trim();
    if (trimmed.length === 0) return;
    setQuestion('');
    void onAsk(trimmed).catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-3">
      {conversationId && (
        <div className="font-mono text-[11px] text-text-secondary">{conversationId}</div>
      )}
      <div className="flex max-h-64 flex-col gap-2 overflow-auto rounded-md border border-border bg-surface/40 p-2">
        {transcript.length === 0 ? (
          <div className="text-xs text-text-secondary">No AI messages yet.</div>
        ) : (
          transcript.map(message => (
            <div
              key={message.id}
              className={
                message.role === 'user'
                  ? 'self-end rounded-md bg-primary/20 px-3 py-2 text-xs text-text-primary'
                  : 'self-start rounded-md bg-surface px-3 py-2 text-xs text-text-primary'
              }
            >
              <span className="block whitespace-pre-wrap">{message.content}</span>
              {message.pending && (
                <span className="mt-1 block text-[11px] text-text-secondary">Streaming…</span>
              )}
            </div>
          ))
        )}
      </div>
      <div className="flex flex-col gap-2">
        <label className="sr-only" htmlFor="artifact-ai-question">
          Ask AI
        </label>
        <Textarea
          id="artifact-ai-question"
          value={question}
          disabled={disabled || isStreaming}
          placeholder="Ask about this artifact"
          onChange={event => {
            setQuestion(event.target.value);
          }}
          className="min-h-24"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit}
            className="inline-flex gap-1.5"
            onClick={submitQuestion}
          >
            <Send className="size-4" />
            {isStreaming ? 'Asking…' : 'Ask'}
          </Button>
        </div>
      </div>
    </div>
  );
}
